import { useEffect, useId, useState } from "react";
import { researchCms, type ResearchProduct } from "@/api/researchCms.service";
import { productSlug } from "@/pages/Research/Cms/utils/productSlug";
import styles from "../CreateRankingEntityModal/CreateRankingEntityModal.module.css";

const NAME_MAX_LENGTH = 120;

type CreateResearchProductModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (product: ResearchProduct) => void;
};

function isConflict(err: unknown): boolean {
  return err instanceof Error && (err as Error & { status?: number }).status === 409;
}

function nameError(name: string): string | null {
  if (name.length > NAME_MAX_LENGTH) return `Name must be ${NAME_MAX_LENGTH} characters or fewer`;
  if (!productSlug(name)) return "Name must include a letter or number";
  return null;
}

export function CreateResearchProductModal({
  open,
  onClose,
  onCreated,
}: CreateResearchProductModalProps) {
  const titleId = useId();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const slug = productSlug(name);

  useEffect(() => {
    if (!open) {
      setName("");
      setFieldError(null);
      setError(null);
    }
  }, [open]);

  // Held open while a request is in flight, like Cancel: closing resets the form, and a failure landing
  // after that would leave its error waiting in the next opening.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !creating) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, creating]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const label = name.trim();
    if (!label) return;
    const invalid = nameError(label);
    setFieldError(invalid);
    if (invalid) return;
    const slugToSend = productSlug(label);
    setCreating(true);
    setError(null);
    try {
      const result = await researchCms.createResearchProduct({ label, slug: slugToSend });
      onCreated(result.product);
      onClose();
    } catch (err) {
      setError(
        isConflict(err)
          ? `A product with the slug "${slugToSend}" already exists. Try a different name.`
          : err instanceof Error
            ? err.message
            : "Failed to add product",
      );
    } finally {
      setCreating(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={e => e.target === e.currentTarget && !creating && onClose()}
    >
      <div
        className={styles.dialog}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId} className={styles.title}>
          Add product
        </h3>
        <p className={styles.description}>
          Adds the product to the list for all editions. Tick it on each edition it applies to.
        </p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="product-name">
              Name{" "}
              <span aria-hidden="true" style={{ color: "var(--color-danger, #ef4444)" }}>
                *
              </span>
            </label>
            <input
              id="product-name"
              className={styles.input}
              value={name}
              onChange={e => {
                setName(e.target.value);
                setFieldError(null);
              }}
              aria-describedby={slug ? "product-slug" : undefined}
              placeholder="e.g. Marketing Toolkit"
              autoFocus
            />
            {slug && (
              <span id="product-slug" className={styles.hint}>
                Slug: {slug}
              </span>
            )}
            {fieldError && <span className={styles.error}>{fieldError}</span>}
          </div>

          {error && (
            <div className={styles.serverError} role="alert">
              {error}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.createButton}
              disabled={creating || !name.trim()}
            >
              {creating ? "Adding..." : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
